import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { ElMessageBox, ElMessage } from "element-plus";
import { useAuthStore } from "~/stores/auth";
import { ServerResponse } from "~/types/generics/serverResponse";

let apiBaseUrl = "";
axios.defaults.withCredentials = true;

export const setAxiosDefaultBearerToken = function (token: string): void {
  axios.defaults.headers.common.Authorization = `Bearer ${token}`;
};

function generateUrl(endPoint: string): string {
  if (apiBaseUrl === "") {
    apiBaseUrl = useRuntimeConfig().public.APP_BASE_URL + "/api";
  }
  const resource = endPoint.replace(/^\/+/, "");
  return `${apiBaseUrl + "/" + resource}`;
}

export const getRequest = async function <T>(
  endPoint: string,
  requiresAuth = true,
  headers: AxiosRequestConfig = {} as AxiosRequestConfig,
  fromApi = true,
) {
  if (requiresAuth) setAxiosDefaultBearerToken(useAuthStore().token as string);
  let response = {} as ServerResponse<T>;
  let error = null as null | AxiosError;
  try {
    if (fromApi) {
      response = await axios.get(generateUrl(endPoint), headers);
    } else {
      response = await axios.get(endPoint, headers);
    }
  } catch (err) {
    error = redirectToSignInIfSessionExpired(err as AxiosError);
  }

  return { response, error };
};

export const postRequest = async function <T>(
  endPoint: string,
  data: any,
  requiresAuth = true,
  headers: AxiosRequestConfig = {} as AxiosRequestConfig,
) {
  if (requiresAuth) setAxiosDefaultBearerToken(useAuthStore().token as string);
  let response = {} as ServerResponse<T>;
  let error = null as null | AxiosError;
  try {
    response = await axios.post(generateUrl(endPoint), data, headers);
  } catch (er) {
    error = redirectToSignInIfSessionExpired(er as AxiosError);
  }

  return { response, error };
};

export const putRequest = async function <T>(
  endPoint: string,
  data: any,
  requiresAuth = true,
  headers: AxiosRequestConfig = {} as AxiosRequestConfig,
) {
  if (requiresAuth) setAxiosDefaultBearerToken(useAuthStore().token as string);
  let response = {} as ServerResponse<T>;
  let error = null as null | AxiosError;
  try {
    response = await axios.put(generateUrl(endPoint), data, headers);
  } catch (er) {
    error = redirectToSignInIfSessionExpired(er as AxiosError);
  }

  return { response, error };
};

export const patchRequest = async function <T>(
  endPoint: string,
  data: any,
  requiresAuth = true,
  headers: AxiosRequestConfig = {} as AxiosRequestConfig,
) {
  if (requiresAuth) setAxiosDefaultBearerToken(useAuthStore().token as string);
  let response = {} as ServerResponse<T>;
  let error = null as null | AxiosError;
  try {
    response = await axios.patch(generateUrl(endPoint), data, headers);
  } catch (er) {
    error = redirectToSignInIfSessionExpired(er as AxiosError);
  }

  return { response, error };
};

export const deleteRequest = async function (
  endPoint: string,
  requiresAuth = true,
) {
  if (requiresAuth) setAxiosDefaultBearerToken(useAuthStore().token as string);
  return await axios
    .delete(generateUrl(endPoint))
    .catch(redirectToSignInIfSessionExpired);
};

export const bulkDeleteRequest = async function <T = any>(
  endPoint: string,
  body: T,
  requiresAuth = true,
) {
  if (requiresAuth) setAxiosDefaultBearerToken(useAuthStore().token as string);

  return await axios
    .delete(generateUrl(endPoint), { data: body })
    .catch(redirectToSignInIfSessionExpired);
};

const CONTENT_TYPE_EXTENSIONS: Record<string, string> = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "application/vnd.ms-excel": ".xls",
  "text/csv": ".csv",
  "application/pdf": ".pdf",
  "application/zip": ".zip",
  "application/json": ".json",
  "text/plain": ".txt",
};

type SaveFilePicker = (options: {
  suggestedName?: string;
  types?: { description?: string; accept: Record<string, string[]> }[];
}) => Promise<{
  createWritable: () => Promise<{
    write: (data: Blob) => Promise<void>;
    close: () => Promise<void>;
  }>;
}>;

const EXTENSION_PATTERN = /^\.[a-z0-9]{1,8}$/i;
const INVALID_FILE_NAME_CHARS = /[\\/:*?"<>|]/g;

function getSaveFilePicker(): SaveFilePicker | undefined {
  if (typeof window === "undefined") return undefined;
  const picker = (window as unknown as { showSaveFilePicker?: SaveFilePicker })
    .showSaveFilePicker;
  return typeof picker === "function" ? picker.bind(window) : undefined;
}

function decodeSafely(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function resolveExtension(headers: Record<string, any>): string {
  const disposition = String(headers?.["content-disposition"] || "");
  const serverName = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const decodedName = serverName ? decodeSafely(serverName[1]) : "";
  const serverExtension = decodedName.includes(".")
    ? decodedName.slice(decodedName.lastIndexOf("."))
    : "";
  if (EXTENSION_PATTERN.test(serverExtension)) return serverExtension;

  const contentType = String(headers?.["content-type"] || "")
    .split(";")[0]
    .trim()
    .toLowerCase();
  return CONTENT_TYPE_EXTENSIONS[contentType] || "";
}

function sanitizeFileName(fileName: string): string {
  return fileName.replace(INVALID_FILE_NAME_CHARS, "-").trim() || "report";
}

function withExtension(fileName: string, extension: string): string {
  if (!extension || fileName.toLowerCase().endsWith(extension.toLowerCase())) {
    return fileName;
  }
  return `${fileName}${extension}`;
}

function saveThroughBrowser(blob: Blob, fileName: string) {
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

async function saveThroughPicker(
  picker: SaveFilePicker,
  blob: Blob,
  fileName: string,
  extension: string,
) {
  const mimeType = blob.type.split(";")[0].trim();
  const handle = await picker({
    suggestedName: fileName,
    types:
      mimeType && extension
        ? [{ description: "Report", accept: { [mimeType]: [extension] } }]
        : undefined,
  });
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
}

async function resolveDownloadErrorMessage(error: any): Promise<string> {
  let body = "";
  try {
    body = (await error?.response?.data?.text()) || "";
  } catch {
    body = "";
  }

  try {
    const parsed = JSON.parse(body);
    const validationError = Object.values(parsed?.errors || {}).flat()[0];
    const message = parsed?.error || parsed?.message || validationError;
    if (message) return String(message);
  } catch {
    const plainText = body.trim();
    if (plainText && !plainText.startsWith("<") && plainText.length <= 500) {
      return plainText;
    }
  }

  return error?.message || "Failed to download the file";
}

async function fetchFile(endPoint: string, requiresAuth: boolean) {
  if (requiresAuth) setAxiosDefaultBearerToken(useAuthStore().token as string);
  const { response, error } = await getRequest<BlobPart>(
    endPoint,
    requiresAuth,
    {
      responseType: "blob",
    },
  );

  if (error) {
    ElMessageBox.alert(await resolveDownloadErrorMessage(error), "Failed", {
      confirmButtonText: "OK",
      showClose: false,
    });
    return null;
  }

  return {
    blob: new Blob([response.data], {
      type: response.headers["content-type"],
    }),
    extension: resolveExtension(response.headers),
  };
}

export const download = async function (
  endPoint: string,
  fileNameWithExt: string,
  requiresAuth = true,
) {
  const file = await fetchFile(endPoint, requiresAuth);
  if (!file) return;

  saveThroughBrowser(file.blob, fileNameWithExt);
};

export const saveBlobWithSavePicker = async function (
  blob: Blob,
  defaultFileName: string,
  extension = "",
) {
  const fileName = sanitizeFileName(withExtension(defaultFileName, extension));
  const picker = getSaveFilePicker();
  if (!picker) {
    saveThroughBrowser(blob, fileName);
    return;
  }

  try {
    await saveThroughPicker(picker, blob, fileName, extension);
    return;
  } catch (error: any) {
    if (error?.name === "AbortError") return;
    if (error?.name !== "SecurityError" && error?.name !== "NotAllowedError") {
      saveThroughBrowser(blob, fileName);
      return;
    }
  }

  const confirmed = await ElMessageBox.confirm(
    `Your report "${fileName}" is ready. Choose where to save it.`,
    "Save Report",
    { confirmButtonText: "Choose Location", cancelButtonText: "Cancel" },
  ).catch(() => null);
  if (!confirmed) return;

  try {
    await saveThroughPicker(picker, blob, fileName, extension);
  } catch (error: any) {
    if (error?.name === "AbortError") return;
    saveThroughBrowser(blob, fileName);
  }
};

export const downloadWithSavePicker = async function (
  endPoint: string,
  defaultFileName: string,
  requiresAuth = true,
) {
  const file = await fetchFile(endPoint, requiresAuth);
  if (!file) return;

  await saveBlobWithSavePicker(file.blob, defaultFileName, file.extension);
};

function redirectToSignInIfSessionExpired(error: AxiosError) {
  if (error.response?.status === 401) {
    useAuthStore().$reset();
    ElMessage({
      message: "login expired",
    });
    useRouter().push("/");
  }
  return error;
}
